import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import useWindowSize from 'src/hooks/useWindowSize';
import AttributesCell from 'src/sections/catalog_products/table_cell/AttributesCell';
import BrandNameCell from 'src/sections/catalog_products/table_cell/BrandNameCell';
import CasePackCell from 'src/sections/catalog_products/table_cell/CasePackCell';
import LastPurchasedInfoIcon from 'src/sections/catalog_products/table_cell/LastPurchasedInfoIcon';
import MfgNumberCell from 'src/sections/catalog_products/table_cell/MfgNumberCell';
import SupplierCell from 'src/sections/catalog_products/table_cell/SupplierCell';
import UomCell from 'src/sections/catalog_products/table_cell/UomCell';
import WeightCell from 'src/sections/catalog_products/table_cell/WeightCell';
import { ShoppingListProductsContext } from 'src/sections/shopping_lists/ShoppingListProductsContext';
import ShoppingListAttributesFilter from 'src/sections/shopping_lists/table_cell/ShoppingListAttributesFilter';
import ShoppingListCatalogCell from 'src/sections/shopping_lists/table_cell/ShoppingListCatalogCell';
import ShoppingListDescriptionCell from 'src/sections/shopping_lists/table_cell/ShoppingListDescriptionCell';
import ShoppingListLastPurchaseDateCell from 'src/sections/shopping_lists/table_cell/ShoppingListLastPurchaseDateCell';
import ShoppingListParCell from 'src/sections/shopping_lists/table_cell/ShoppingListParCell';
import ShoppingListPriceCell from 'src/sections/shopping_lists/table_cell/ShoppingListPriceCell';
import ShoppingListQtyCell from 'src/sections/shopping_lists/table_cell/ShoppingListQtyCell';
import ShoppingListReorderHandleCell from 'src/sections/shopping_lists/table_cell/ShoppingListReorderHandleCell';
import ShoppingListSwapItem from 'src/sections/shopping_lists/table_cell/ShoppingListSwapItem';
import { useContextSelector } from 'use-context-selector';

const useShoppingListProductColumns = () => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const canReorderRows = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.canReorderRows
  );

  const gridWidth = useMemo(() => Math.max(width, 1200), [width]);

  const setPercentage = useMemo(
    () => (percentage: number) => Math.round((gridWidth / 100) * percentage),
    [gridWidth]
  );

  const columns: GridColumn<Record<string, unknown>>[] = useMemo(() => {
    const reorderColumn: GridColumn<Record<string, unknown>> = {
      field: 'reorder',
      title: '',
      menu: false,
      sortable: false,
      filterable: false,
      width: 40,
      show: true,
      component: ShoppingListReorderHandleCell,
    };

    const productColumns: GridColumn<Record<string, unknown>>[] = [
      {
        field: 'selected',
        title: '',
        filter: 'boolean',
        menu: false,
        sortable: false,
      },
      {
        field: 'isNoItem',
        title: '',
        menu: false,
        sortable: false,
        width: 40,
        component: ShoppingListSwapItem,
      },
      {
        field: 'qty',
        title: t('Qty'),
        sortable: false,
        filterable: false,
        menu: false,
        width: setPercentage(7),
        show: true,
        component: ShoppingListQtyCell,
      },
      {
        field: 'attributes',
        title: t('Attributes'),
        filter: 'text',
        sortable: false,
        menu: true,
        width: setPercentage(9),
        show: true,
        component: AttributesCell,
        columnMenu: ShoppingListAttributesFilter,
      },
      {
        field: 'itemNum',
        title: t('Item #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(8),
        show: true,
      },
      {
        field: 'brandName',
        title: t('Brand Name'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(9),
        show: true,
        component: BrandNameCell,
      },
      {
        field: 'description',
        title: t('Description'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(16),
        show: true,
        component: ShoppingListDescriptionCell,
      },
      {
        field: 'catalogName',
        title: t('Catalog'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(10),
        show: true,
        component: ShoppingListCatalogCell,
      },
      {
        field: 'companyName',
        title: t('Supplier'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(9),
        show: true,
        component: SupplierCell,
      },
      {
        field: 'uom',
        title: t('UOM'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
        component: UomCell,
      },
      {
        field: 'casePack',
        title: t('Case Pack'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
        component: CasePackCell,
      },
      {
        field: 'manufacturerNumber',
        title: t('Mfg #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
        component: MfgNumberCell,
      },
      {
        field: 'price',
        title: t('Price'),
        filter: 'numeric',
        format: '{0:c2}',
        filterFormat: 'c',
        dataType: 'dollar',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
        component: ShoppingListPriceCell,
      },
      {
        field: 'weight',
        title: t('Wt.'),
        filter: 'text',
        dataType: 'string',
        sortable: true,
        menu: true,
        width: setPercentage(7),
        show: true,
        component: WeightCell,
      },
      {
        field: 'par',
        title: t('PAR'),
        filter: 'numeric',
        dataType: 'number',
        sortable: true,
        menu: true,
        width: setPercentage(6),
        show: true,
        component: ShoppingListParCell,
      },
      {
        field: 'lastPurchaseDate',
        headerClassName: 'cursor-pointer',
        headerCell: () => (
          <>
            <span
              className="k-column-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={t('Shopping List Last Purchased')}
            >
              {t('Shopping List Last Purchased')}
            </span>
            <LastPurchasedInfoIcon />
          </>
        ),
        filter: 'date',
        dataType: 'date',
        sortable: true,
        menu: true,
        width: setPercentage(12),
        show: true,
        component: ShoppingListLastPurchaseDateCell,
      },
    ];

    return canReorderRows ? [reorderColumn, ...productColumns] : productColumns;
  }, [canReorderRows, setPercentage, t]);

  return columns;
};

export default useShoppingListProductColumns;
